import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import { useDataFetchIndicator } from '..';

import vopayApi from '../../services/api.config';

type HookProps = {
  api: vp.Api;
  bearer?: string;
};

type ReturnTuple = [
  vp.CreateAccountUserResponse,
  (requestParams: vp.CreateAccountUserRequest) => void,
  (requestParams: vp.CreateAccountUserRequest, bearer?: string) => Promise<vp.CreateAccountUserResponse>,
  () => void,
  (_params: vp.CreateAccountUserRequest) => Promise<vp.CreateAccountUserResponse>,
];

function usePostCreateAccountUser({ api, bearer }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.CreateAccountUserRequest>();
  const [response, setResponse] = useState<vp.CreateAccountUserResponse>({
    Success: false,
    ErrorMessage: '',
    Username: '',
    Password: '',
  });
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: vp.CreateAccountUserRequest) => {
      setDataFetchingInfo(true, 'create account', 'fullScreen');
      const res = await api.postCreateAccountUser(requestParams, bearer, source);

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: vp.CreateAccountUserRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    async (requestParams: vp.CreateAccountUserRequest, bearerOverride?: string) => {
      const res = await api.postCreateAccountUser(requestParams, bearerOverride ?? bearer, source);
      return res;
    },
    () => {
      setResponse({
        Success: false,
        ErrorMessage: '',
        Username: '',
        Password: '',
      });
      setSendRequest(false);
    },
    (_params: vp.CreateAccountUserRequest) => api.postCreateAccountUser(_params, bearer),
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostCreateAccountUser);
